import type { EntryKey } from "@pinia/colada";
import type { OrganizationCredits } from "@/schemas/organization.schema";
import { useQuery, useQueryCache } from "@pinia/colada";
import { api } from "@/services/api";
import { useUserProfile } from "@/stores/user-profile.store";

/**
 * Query keys factory for organization following Pinia Colada best practices
 *
 * @see https://pinia-colada.esm.dev/guide/query-keys.html
 */
export const ORGANIZATION_QUERY_KEYS = {
  root: ["organization"] as const,
  credits: () => [...ORGANIZATION_QUERY_KEYS.root, "credits"] as const,
};

/**
 * Query hook for fetching organization credits
 *
 * Features:
 * - Smart caching with 30s stale time
 * - Automatic deduplication of concurrent requests
 * - Shows current credit balance and currency
 *
 * @example
 * ```ts
 * const creditsQuery = useOrgCreditsQuery();
 *
 * // In template
 * if (creditsQuery.isLoading.value) return <Skeleton />;
 * const credits = creditsQuery.data.value?.credits ?? 0;
 * ```
 */
export function useOrgCreditsQuery() {
  const userProfile = useUserProfile();

  return useQuery({
    key: ORGANIZATION_QUERY_KEYS.credits() as unknown as EntryKey,
    query: async () => {
      const { data } = await api.get<OrganizationCredits>("/organization/credits");
      return data;
    },
    staleTime: 30_000, // 30s - credits don't change frequently
    gcTime: 5 * 60_000, // 5 minutes - garbage collection time
    // Only fetch for organization-scoped roles (owner, admin)
    enabled: () => userProfile.user?.role_scope === "organization",
  });
}

/**
 * Helper hook to invalidate organization queries
 *
 * Use this after mutations that affect organization data (add credits, update settings)
 *
 * @example
 * ```ts
 * const invalidate = useInvalidateOrgCredits();
 *
 * // After adding credits
 * const handleAddCredits = async () => {
 *   await addCredits(...);
 *   invalidate.credits();
 * };
 * ```
 */
export function useInvalidateOrgCredits() {
  const queryCache = useQueryCache();

  return {
    /**
     * Invalidate all organization queries
     */
    all: () => queryCache.invalidateQueries({ key: ORGANIZATION_QUERY_KEYS.root as any }),

    /**
     * Invalidate only credits query
     */
    credits: () => queryCache.invalidateQueries({ key: ORGANIZATION_QUERY_KEYS.credits() as any }),
  };
}
